// ResetPasswordPage.jsx
import React, { useEffect, useState } from "react";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { auth } from "./firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle, CheckCircle, Lock } from "lucide-react";

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function ResetPasswordPage() {
  const query = useQuery();
  const navigate = useNavigate();

  const oobCode = query.get("oobCode");
  const mode = query.get("mode"); // should be "resetPassword"

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const run = async () => {
      setError("");
      if (!oobCode || mode !== "resetPassword") {
        setError("Link i pavlefshëm ose i skaduar.");
        setLoading(false);
        return;
      }
      try {
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
      } catch (err) {
        console.error("verifyPasswordResetCode error:", err);
        setError("Ky link për rivendosjen e fjalëkalimit është i pavlefshëm ose ka skaduar.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [oobCode, mode]);

  const handleSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError("Fjalëkalimi duhet të ketë të paktën 6 karaktere.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Fjalëkalimet nuk përputhen.");
      return;
    }
    try {
      setSubmitting(true);
      setError("");
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess("Fjalëkalimi u rivendos me sukses. Tani mund të bësh hyrje.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      console.error("confirmPasswordReset error:", err);
      setError("Nuk u arrit të rivendoset fjalëkalimi. Provo përsëri.");
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 30%, #f0fdf4 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
      fontFamily: "'Inter', sans-serif",
    },
    card: {
      backgroundColor: "rgba(255, 255, 255, 0.96)",
      borderRadius: 24,
      padding: 32,
      width: "100%",
      maxWidth: 440,
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.18)",
      backdropFilter: "blur(20px)",
    },
    title: {
      fontSize: 24,
      fontWeight: 700,
      marginBottom: 8,
      textAlign: "center",
    },
    subtitle: {
      fontSize: 14,
      color: "#6b7280",
      textAlign: "center",
      marginBottom: 20,
    },
    fieldLabel: {
      fontSize: 13,
      fontWeight: 500,
      marginBottom: 6,
      color: "#4b5563",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      backgroundColor: "#f9fafb",
      fontSize: 15,
      marginBottom: 12,
      outline: "none",
    },
    button: {
      width: "100%",
      background: "linear-gradient(135deg, #3b82f6, #6366f1)",
      color: "white",
      padding: "12px 16px",
      borderRadius: 12,
      border: "none",
      fontSize: 15,
      fontWeight: 600,
      marginTop: 8,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },
    alert: {
      padding: 12,
      borderRadius: 12,
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 16,
    },
    error: { backgroundColor: "#fee2e2", color: "#b91c1c" },
    success: { backgroundColor: "#dcfce7", color: "#15803d" },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Vendos një fjalëkalim të ri</h1>
        {email && (
          <p style={styles.subtitle}>
            Për llogarinë: <strong>{email}</strong>
          </p>
        )}

        {loading && <p style={styles.subtitle}>Duke verifikuar linkun…</p>}

        {!loading && error && (
          <div style={{ ...styles.alert, ...styles.error }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {success && (
              <div style={{ ...styles.alert, ...styles.success }}>
                <CheckCircle size={16} /> {success}
              </div>
            )}

            {!success && (
              <>
                <label style={styles.fieldLabel}>Fjalëkalim i ri</label>
                <input
                  type="password"
                  style={styles.input}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Shkruaj fjalëkalimin e ri"
                />

                <label style={styles.fieldLabel}>Përsërit fjalëkalimin</label>
                <input
                  type="password"
                  style={styles.input}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Përsërit fjalëkalimin"
                />

                <button
                  style={styles.button}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  <Lock size={18} />
                  {submitting ? "Duke ruajtur..." : "Ruaj fjalëkalimin"}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
